// TransactionsModal — drill from a data point to the raw underlying invoice lines.
// Serves /api/transactions for the CURRENT Explore filter (tenant scope + runId are
// inherited server-side). Reuses the Pipeline .scrim/.modal + .scroll-x/table.dense
// pattern. Client-side search over the fetched rows + Copy/Download CSV. The header
// shows "showing N of Total" so a capped result is never mistaken for the whole set.

import { useEffect, useMemo, useState } from "react";
import { AnalyticsApi } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";
import { fmtN } from "../lib/format.js";

const PAGE = 500;   // rows per page (server-side paginated via offset)
// Columns the endpoint returns that are numeric → right-align + thousands format.
const NUM_COLS = new Set(["unit", "unit_price", "price_ex_vat", "vat", "price", "cost"]);
const fmtCell = (col, v) => {
  if (v === null || v === undefined || v === "") return "";
  if (NUM_COLS.has(col) && typeof v === "number") return v.toLocaleString("en-ZA", { maximumFractionDigits: 2 });
  return String(v);
};

// RFC-4180-ish CSV: quote fields containing comma/quote/newline, double embedded quotes.
const csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function TransactionsModal({ filter, runId, title = "Transactions", subtitle, onClose }) {
  const [data, setData] = useState(null);   // { columns, rows, total, limit, offset }
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);   // server-side page offset (rows)

  // Reset to the first page whenever the underlying filter/run changes.
  useEffect(() => { setOffset(0); }, [filter, runId]);   // eslint-disable-line react-hooks/set-state-in-effect
  // Fetch the current page. Keep the prior page visible while the next loads so the
  // search box + pager don't flash away on each Prev/Next (only the first load spins).
  useEffect(() => {
    let alive = true;
    setError(null);   // eslint-disable-line react-hooks/set-state-in-effect
    AnalyticsApi.transactions(filter, runId, PAGE, offset)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.body?.error ?? e.message); });
    return () => { alive = false; };
  }, [filter, runId, offset]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.rows;
    return data.rows.filter((row) => row.some((v) => v != null && String(v).toLowerCase().includes(needle)));
  }, [data, q]);

  const downloadCsv = () => {
    if (!data) return;
    const lines = [data.columns.map(csvCell).join(",")];
    for (const row of filtered) lines.push(row.map(csvCell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "transactions.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const total = data?.total ?? 0;
  const pageRows = data?.rows.length ?? 0;
  const start = total === 0 ? 0 : offset + 1;
  const end = offset + pageRows;
  const pageNo = Math.floor(offset / PAGE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE));
  const canPrev = offset > 0;
  const canNext = end < total;

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="mh-ic"><Icon name="table" size={20} /></div>
          <div>
            <h2>{title}</h2>
            <div className="sub">{subtitle ?? "Raw invoice lines behind the current filter"}</div>
          </div>
          <button type="button" className="x" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="error"><Icon name="alert" size={15} /> {error}</div>}
          {!error && !data && (
            <p className="hint" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="spinner" style={{ color: "var(--primary)" }} /> Loading transactions…
            </p>
          )}
          {data && (
            <>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                <div style={{ flex: "1 1 220px", minWidth: 180, display: "flex", alignItems: "center", gap: 8,
                  border: "1px solid var(--line)", borderRadius: "var(--r-card)", padding: "7px 11px", color: "var(--muted)" }}>
                  <Icon name="search" size={15} />
                  <input type="search" placeholder="Search these rows…" value={q}
                    onChange={(e) => setQ(e.target.value)}
                    style={{ width: "100%", border: "none", background: "transparent", outline: "none", color: "var(--ink)", fontSize: 13 }} />
                </div>
                <button type="button" className="btn ghost sm" onClick={downloadCsv} disabled={!filtered.length}>
                  <Icon name="download" size={14} /> Download CSV
                </button>
              </div>

              {filtered.length === 0 ? (
                <p className="hint" style={{ marginTop: 14 }}>No transactions match.</p>
              ) : (
                <div className="scroll-x" style={{ marginTop: 10, maxHeight: "56vh", overflowY: "auto" }}>
                  <table className="dense">
                    <thead>
                      <tr>{data.columns.map((c) => (
                        <th key={c} className={NUM_COLS.has(c) ? "num" : undefined}>{c}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, i) => (
                        <tr key={i}>
                          {row.map((v, j) => (
                            <td key={j} className={`${NUM_COLS.has(data.columns[j]) ? "num" : ""}${v === null ? " null-cell" : ""}`}>
                              {fmtCell(data.columns[j], v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                <p className="hint" style={{ margin: 0, flex: "1 1 auto" }}>
                  {q
                    ? <>Showing <b>{fmtN(filtered.length)}</b> of {fmtN(pageRows)} on this page · <b>{fmtN(total)}</b> total</>
                    : <>Showing <b>{fmtN(start)}–{fmtN(end)}</b> of <b>{fmtN(total)}</b></>}
                </p>
                {pageCount > 1 && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button type="button" className="btn ghost sm" disabled={!canPrev}
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>
                      <Icon name="arrowR" size={13} style={{ transform: "rotate(180deg)" }} /> Prev
                    </button>
                    <span className="hint" style={{ whiteSpace: "nowrap" }}>Page {fmtN(pageNo)} of {fmtN(pageCount)}</span>
                    <button type="button" className="btn ghost sm" disabled={!canNext}
                      onClick={() => setOffset((o) => o + PAGE)}>
                      Next <Icon name="arrowR" size={13} />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
